import {
  canEdit,
  removeChecklistItem,
  toggleCheck,
  updateInstanceChecklist,
} from '../store.js';
import {
  CHECKLIST_CATEGORY_ORDER,
  checklistCategoryLabel,
} from '../config/checklist-categories.js';
import { escapeHtml } from '../utils/dom.js';
import { authorChip } from '../utils/author.js';
import { openChecklistAddModal } from '../utils/checklist-add-modal.js';
import { resolveTripBases } from '../utils/trip-bases.js';
import { getContext, patchChecklistItems } from '../trip-context.js';
import { bindTripShell, getTripMainEl, renderTripShell } from './shell.js';

function sortCategories(categories) {
  const order = new Map(CHECKLIST_CATEGORY_ORDER.map((cat, i) => [cat, i]));
  return [...categories].sort((a, b) => {
    const ai = order.has(a) ? order.get(a) : 999;
    const bi = order.has(b) ? order.get(b) : 999;
    if (ai !== bi) return ai - bi;
    return a.localeCompare(b);
  });
}

function renderAddTrigger() {
  if (!canEdit()) return '';

  return `
    <div class="checklist-add-area" data-checklist-add-area>
      <button type="button" class="custom-add-trigger" data-open-checklist-add aria-expanded="false">
        <span class="custom-add-trigger__icon" aria-hidden="true">+</span>
        <span class="custom-add-trigger__label">Aggiungi voce alla lista</span>
      </button>
    </div>
  `;
}

function renderChecklistContent(ctx) {
  const checksByKey = new Map(ctx.live.checks.map((c) => [c.item_key, c]));
  const byCategory = new Map();

  for (const item of ctx.checklistItems) {
    const cat = item.category ?? 'altro';
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat).push(item);
  }

  const sections = sortCategories(byCategory.keys())
    .map((category) => {
      const items = byCategory.get(category) ?? [];
      const rows = items
        .map((item) => {
          const check = checksByKey.get(item.itemKey);
          const checked = check?.checked ?? false;
          const chip = check?.updated_by
            ? authorChip(ctx.live.members, check.updated_by, check.updated_at)
            : '';
          const editable = canEdit();

          return `
            <div class="checklist-item" data-item-key="${escapeHtml(item.itemKey)}">
              <input
                type="checkbox"
                class="checklist-item__input"
                data-item-key="${escapeHtml(item.itemKey)}"
                ${checked ? 'checked' : ''}
                ${editable ? '' : 'disabled'}
                aria-label="Segna come fatto: ${escapeHtml(item.label)}"
              />
              ${
                editable
                  ? `<input
                      type="text"
                      class="checklist-item__label-input"
                      data-item-key="${escapeHtml(item.itemKey)}"
                      value="${escapeHtml(item.label)}"
                      aria-label="Testo voce"
                    />`
                  : `<span class="checklist-item__label">${escapeHtml(item.label)}</span>`
              }
              ${
                editable
                  ? `<button
                      type="button"
                      class="checklist-item__remove"
                      data-item-key="${escapeHtml(item.itemKey)}"
                      aria-label="Rimuovi voce"
                      title="Rimuovi"
                    >×</button>`
                  : ''
              }
              ${chip ? `<span class="checklist-item__author">${chip}</span>` : ''}
            </div>
          `;
        })
        .join('');

      return `
        <section class="checklist-group">
          <h2 class="checklist-group__title">${escapeHtml(checklistCategoryLabel(category))}</h2>
          ${rows}
        </section>
      `;
    })
    .join('');

  return `
    ${sections || '<p class="checklist-empty">Nessuna voce ancora.</p>'}
    ${renderAddTrigger()}
  `;
}

async function persistChecklist(ctx, items) {
  const updated = await updateInstanceChecklist(ctx.instance.id, items);
  patchChecklistItems(items, updated);
}

function bindChecklist(main, ctx, rootContainer) {
  main.querySelector('[data-open-checklist-add]')?.addEventListener('click', () => {
    if (!canEdit()) return;
    openChecklistAddModal({
      instanceId: ctx.instance.id,
      checklistItems: ctx.checklistItems,
      onSuccess: () => {
        if (rootContainer) updateChecklistMain(rootContainer, getContext());
      },
    });
  });

  main.querySelectorAll('.checklist-item__input').forEach((input) => {
    input.addEventListener('change', async () => {
      if (!canEdit()) return;
      try {
        await toggleCheck(ctx.instance.id, input.dataset.itemKey, input.checked);
      } catch (error) {
        input.checked = !input.checked;
        alert(error.message);
      }
    });
  });

  main.querySelectorAll('.checklist-item__label-input').forEach((input) => {
    input.addEventListener('change', async () => {
      if (!canEdit()) return;
      const label = input.value.trim();
      if (!label) {
        input.value =
          ctx.checklistItems.find((item) => item.itemKey === input.dataset.itemKey)?.label ?? '';
        return;
      }
      const next = ctx.checklistItems.map((item) =>
        item.itemKey === input.dataset.itemKey ? { ...item, label } : item,
      );
      try {
        await persistChecklist(ctx, next);
      } catch (error) {
        alert(error.message);
      }
    });
  });

  main.querySelectorAll('.checklist-item__remove').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!canEdit()) return;
      const itemKey = btn.dataset.itemKey;
      const item = ctx.checklistItems.find((row) => row.itemKey === itemKey);
      if (!item) return;
      if (!confirm(`Rimuovere «${item.label}» dalla lista?`)) return;
      try {
        const updated = await removeChecklistItem(ctx.instance.id, itemKey, ctx.checklistItems);
        patchChecklistItems(updated.checklist_items, updated);
        if (rootContainer) updateChecklistMain(rootContainer, getContext());
      } catch (error) {
        alert(error.message);
      }
    });
  });
}

export function renderChecklist(container, ctx) {
  container.innerHTML = renderTripShell({
    trip: ctx.trip,
    tripId: ctx.tripId,
    activeTab: 'checklist',
    mainHtml: renderChecklistContent(ctx),
    members: ctx.live.members,
    bases: resolveTripBases(ctx.live),
  });
  bindTripShell(container, {
    tripId: ctx.tripId,
    instanceId: ctx.instance.id,
    tripTitle: ctx.trip.title,
  });
  bindChecklist(getTripMainEl(container), ctx, container);
}

export function updateChecklistMain(container, ctx) {
  const main = getTripMainEl(container);
  if (!main) return;
  main.innerHTML = renderChecklistContent(ctx);
  bindChecklist(main, ctx, container);
}
